import React, { useEffect, useState } from "react";
import * as THREE from "three";
import { loadImage, swatchUrl } from "./tileTexture";

const Ceramic = ({ c = "#f7f6f2" }) => <meshPhysicalMaterial color={c} roughness={0.15} clearcoat={1} clearcoatRoughness={0.1} />;
const Metal = ({ c = "#1b1b1b" }) => <meshStandardMaterial color={c} metalness={0.85} roughness={0.28} />;
const Glass = () => <meshPhysicalMaterial color="#cfe3e8" transparent opacity={0.22} roughness={0.05} metalness={0.1} />;
const Matte = ({ c }) => <meshStandardMaterial color={c} roughness={0.7} />;

const useImgTex = (url) => {
  const [t, setT] = useState(null);
  useEffect(() => {
    let alive = true;
    loadImage(swatchUrl(url)).then((img) => {
      if (!alive || !img) return;
      const tx = new THREE.Texture(img);
      tx.colorSpace = THREE.SRGBColorSpace;
      tx.needsUpdate = true;
      setT(tx);
    });
    return () => { alive = false; };
  }, [url]);
  return t;
};

const Tap = ({ z, metal, y = 0.9 }) => (
  <group position={[0, y, z]}>
    <mesh position={[0, 0.12, 0]}><cylinderGeometry args={[0.015, 0.015, 0.24, 12]} /><Metal c={metal} /></mesh>
    <mesh position={[0, 0.235, 0.06]} rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[0.011, 0.011, 0.13, 12]} /><Metal c={metal} /></mesh>
  </group>
);

const Bath = ({ w, d, h, metal }) => (
  <group>
    <mesh position={[0, h / 2, 0]} castShadow receiveShadow><boxGeometry args={[w, h, d]} /><Ceramic /></mesh>
    <mesh position={[0, h - 0.015, 0.02]}><boxGeometry args={[w - 0.14, 0.04, d - 0.16]} /><meshStandardMaterial color="#d9e2e6" roughness={0.2} /></mesh>
    <Tap z={-d / 2 + 0.05} y={h - 0.05} metal={metal} />
  </group>
);

const FreeBath = ({ w, d, h, m, metal }) => (
  <group>
    <mesh position={[0, h / 2, 0]} scale={[w, 1, d]} castShadow><cylinderGeometry args={[0.5, 0.44, h, 48]} /><Ceramic c={m.colour || "#f7f6f2"} /></mesh>
    <mesh position={[0, h - 0.01, 0]} scale={[w - 0.12, 1, d - 0.12]}><cylinderGeometry args={[0.5, 0.5, 0.03, 48]} /><meshStandardMaterial color="#e6ecef" roughness={0.2} /></mesh>
    <mesh position={[0, 0.5, -d / 2 - 0.08]}><cylinderGeometry args={[0.016, 0.016, 1, 12]} /><Metal c={metal} /></mesh>
  </group>
);

const Tray = ({ w, d }) => <mesh position={[0, 0.02, 0]} receiveShadow castShadow><boxGeometry args={[w, 0.04, d]} /><meshStandardMaterial color="#55595d" roughness={0.85} /></mesh>;

const Screen = ({ w, h, x = 0, z = 0, metal }) => (
  <group position={[x, 0, z]}>
    <mesh position={[0, h / 2 + 0.04, 0]}><boxGeometry args={[w, h, 0.01]} /><Glass /></mesh>
    <mesh position={[0, h + 0.04, 0]}><boxGeometry args={[w, 0.025, 0.025]} /><Metal c={metal} /></mesh>
    <mesh position={[w / 2, h / 2 + 0.04, 0]}><boxGeometry args={[0.025, h, 0.025]} /><Metal c={metal} /></mesh>
  </group>
);

const ShowerHead = ({ d, h, metal, tray = false }) => (
  <group>
    <mesh position={[0, h / 2 + 0.05, -d / 2 + 0.03]}><cylinderGeometry args={[0.014, 0.014, h, 12]} /><Metal c={metal} /></mesh>
    <mesh position={[0, h + 0.05, -d / 2 + 0.03 + 0.15]}><boxGeometry args={[0.02, 0.02, 0.3]} /><Metal c={metal} /></mesh>
    <mesh position={[0, h + 0.03, -d / 2 + 0.3]}><boxGeometry args={[0.3, 0.012, 0.3]} /><Metal c={metal} /></mesh>
    <mesh position={[0, 1.1, -d / 2 + 0.03]}><boxGeometry args={[0.14, 0.2, 0.05]} /><Metal c={metal} /></mesh>
  </group>
);

const WalkIn = ({ w, d, h, metal }) => (
  <group>
    <Tray w={w} d={d} />
    <Screen w={w * 0.6} h={h - 0.05} x={-w * 0.2} z={d / 2 - 0.02} metal={metal} />
    <group position={[w * 0.15, 0, 0]}><ShowerHead d={d} h={h} metal={metal} /></group>
  </group>
);

const Toilet = ({ w, d }) => (
  <group>
    <mesh position={[0, 0.4, -d / 2 + 0.1]} castShadow><boxGeometry args={[w + 0.04, 0.8, 0.2]} /><Ceramic /></mesh>
    <mesh position={[0, 0.2, 0.06]} scale={[1, 1, 1.35]} castShadow><cylinderGeometry args={[0.17, 0.13, 0.4, 32]} /><Ceramic /></mesh>
    <mesh position={[0, 0.41, 0.06]} scale={[1, 1, 1.35]}><cylinderGeometry args={[0.18, 0.18, 0.025, 32]} /><Ceramic /></mesh>
  </group>
);

const ToiletCC = ({ w, d }) => (
  <group>
    <mesh position={[0, 0.2, 0.08]} scale={[1, 1, 1.35]} castShadow><cylinderGeometry args={[0.17, 0.12, 0.4, 32]} /><Ceramic /></mesh>
    <mesh position={[0, 0.41, 0.08]} scale={[1, 1, 1.35]}><cylinderGeometry args={[0.18, 0.18, 0.025, 32]} /><Ceramic /></mesh>
    <mesh position={[0, 0.62, -d / 2 + 0.09]} castShadow><boxGeometry args={[w, 0.4, 0.17]} /><Ceramic /></mesh>
  </group>
);

const ToiletWall = ({ w, d, m, metal }) => (
  <group>
    <mesh position={[0, 0.55, -d / 2 + 0.075]}><boxGeometry args={[w + 0.25, 1.1, 0.15]} /><Matte c="#e9e4da" /></mesh>
    <mesh position={[0, 0.3, 0.05]} scale={[1, 1, 1.4]} castShadow><cylinderGeometry args={[0.18, 0.12, 0.26, 32]} /><Ceramic /></mesh>
    <mesh position={[0, 0.44, 0.05]} scale={[1, 1, 1.4]}><cylinderGeometry args={[0.19, 0.19, 0.025, 32]} /><Ceramic /></mesh>
    <mesh position={[0, 0.95, -d / 2 + 0.152]}><boxGeometry args={[0.2, 0.12, 0.01]} /><Metal c={metal} /></mesh>
    {m.smart && <mesh position={[w / 2 + 0.02, 0.48, 0.05]}><boxGeometry args={[0.04, 0.05, 0.2]} /><Metal c="#c9ced2" /></mesh>}
  </group>
);

const BasinCounter = ({ w, d, m, metal }) => (
  <group>
    <mesh position={[0, 0.8, 0]} castShadow><boxGeometry args={[w, 0.05, d]} /><Matte c="#9a8068" /></mesh>
    {m.shape === "rect"
      ? <mesh position={[0, 0.885, 0.03]} castShadow><boxGeometry args={[0.48, 0.12, 0.34]} /><Ceramic c={m.colour} /></mesh>
      : <mesh position={[0, 0.885, 0.03]} castShadow><cylinderGeometry args={[0.2, 0.15, 0.12, 40]} /><Ceramic c={m.colour} /></mesh>}
    <Tap z={-d / 2 + 0.05} y={0.825} metal={metal} />
  </group>
);

const Vanity = ({ w, d, m, metal }) => {
  const xs = m.double ? [-w / 4, w / 4] : [0];
  return (
    <group>
      <mesh position={[0, 0.6, 0]} castShadow><boxGeometry args={[w, 0.48, d]} /><Matte c={m.colour || "#8d8378"} /></mesh>
      {xs.map((x) => (
        <group key={x} position={[x, 0, 0]}>
          <mesh position={[0, 0.6, d / 2 + 0.004]}><boxGeometry args={[0.18, 0.015, 0.012]} /><Metal c={m.handle || metal} /></mesh>
          <mesh position={[0, 0.9, 0.03]} castShadow><cylinderGeometry args={[0.19, 0.16, 0.12, 40]} /><Ceramic /></mesh>
          <Tap z={-d / 2 + 0.05} y={0.84} metal={metal} />
        </group>
      ))}
    </group>
  );
};

const TowelRail = ({ w, d, m }) => (
  <group position={[0, 0, -d / 2 + 0.04]}>
    {[-w / 2, w / 2].map((x) => <mesh key={x} position={[x, 0.9, 0]}><cylinderGeometry args={[0.014, 0.014, 1.2, 12]} /><Metal c={m.colour} /></mesh>)}
    {Array.from({ length: 9 }).map((_, i) => <mesh key={i} position={[0, 0.35 + i * 0.135, 0]} rotation={[0, 0, Math.PI / 2]}><cylinderGeometry args={[0.009, 0.009, w, 8]} /><Metal c={m.colour} /></mesh>)}
  </group>
);

const Mirror = ({ w, d }) => (
  <group position={[0, 1.55, -d / 2 + 0.02]}>
    <mesh rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[w / 2, w / 2, 0.02, 64]} /><meshStandardMaterial color="#c8d4da" metalness={1} roughness={0.05} /></mesh>
    <mesh position={[0, 0, 0.012]}><torusGeometry args={[w / 2 - 0.02, 0.008, 8, 64]} /><meshStandardMaterial color="#fff6dc" emissive="#fff1c4" emissiveIntensity={1.5} /></mesh>
  </group>
);

const MirrorRect = ({ w, h, d }) => (
  <group position={[0, 1.5, -d / 2 + 0.02]}>
    <mesh><boxGeometry args={[w, h, 0.02]} /><meshStandardMaterial color="#c8d4da" metalness={1} roughness={0.05} /></mesh>
    <mesh position={[0, 0, -0.012]}><boxGeometry args={[w + 0.03, h + 0.03, 0.005]} /><meshStandardMaterial color="#fff6dc" emissive="#fff1c4" emissiveIntensity={1.2} /></mesh>
  </group>
);

const Niche = ({ w, h, d, m, metal }) => {
  const y = (m.elev || 1) + h / 2;
  const z = -d / 2;
  return (
    <group position={[0, y, z]}>
      <mesh position={[0, 0, 0.004]}><planeGeometry args={[w, h]} /><meshStandardMaterial color="#2b2723" roughness={0.6} /></mesh>
      {[[0, h / 2, w + 0.02, 0.012], [0, -h / 2, w + 0.02, 0.012]].map(([x, yy, ww, hh]) => <mesh key={yy} position={[x, yy, 0.01]}><boxGeometry args={[ww, hh, 0.012]} /><Metal c={metal} /></mesh>)}
      {[-w / 2, w / 2].map((x) => <mesh key={x} position={[x, 0, 0.01]}><boxGeometry args={[0.012, h, 0.012]} /><Metal c={metal} /></mesh>)}
      {m.shelf && <mesh position={[0, 0, 0.03]}><boxGeometry args={[w, 0.012, 0.06]} /><meshPhysicalMaterial color="#e9e4dc" roughness={0.2} /></mesh>}
      <mesh position={[0, -h / 2 + 0.006, 0.03]}><boxGeometry args={[w, 0.008, 0.06]} /><meshStandardMaterial color="#4a443e" /></mesh>
    </group>
  );
};

const KitchenUnit = ({ w, d, h, m }) => {
  const tex = useImgTex(m.swatch);
  const doors = Math.max(1, Math.round(w / 0.6));
  return (
    <group>
      <mesh position={[0, 0.05, 0.03]}><boxGeometry args={[w, 0.1, d - 0.06]} /><Matte c="#222" /></mesh>
      <mesh position={[0, 0.1 + (h - 0.14) / 2, 0]} castShadow><boxGeometry args={[w, h - 0.14, d]} /><meshStandardMaterial key={tex?.uuid || "n"} map={tex} color={tex ? "#fff" : "#ddd"} roughness={0.6} /></mesh>
      {Array.from({ length: doors - 1 }).map((_, i) => <mesh key={i} position={[-w / 2 + ((i + 1) * w) / doors, 0.1 + (h - 0.14) / 2, d / 2 + 0.002]}><boxGeometry args={[0.004, h - 0.16, 0.004]} /><Matte c="#333" /></mesh>)}
    </group>
  );
};

const Worktop = ({ w, d, h, m }) => {
  const tex = useImgTex(m.swatch);
  return <mesh position={[0, 0.9 + h / 2, 0]} castShadow><boxGeometry args={[w, h, d]} /><meshStandardMaterial key={tex?.uuid || "n"} map={tex} color={tex ? "#fff" : "#ccc"} roughness={0.3} /></mesh>;
};

const MODELS = {
  bath: Bath, freestanding_bath: FreeBath, walkin_shower: WalkIn, shower_tray: Tray, shower_screen: Screen, shower_head: ShowerHead,
  toilet: Toilet, toilet_cc: ToiletCC, toilet_wall: ToiletWall, basin_counter: BasinCounter, vanity: Vanity, towel_rail: TowelRail,
  mirror: Mirror, mirror_rect: MirrorRect, niche: Niche, kitchen_unit: KitchenUnit, worktop: Worktop,
};

export const Fixture3D = ({ f, meta, metal, selected, onPointerDown }) => {
  const Model = meta && MODELS[meta.model];
  if (!Model) return null;
  return (
    <group position={[f.x, 0, f.z]} rotation={[0, (f.rot * Math.PI) / 180, 0]} onPointerDown={onPointerDown}>
      <Model w={meta.w} d={meta.d} h={meta.h} m={meta} metal={metal} />
      {selected && (
        <mesh position={[0, 0.006, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[meta.w + 0.06, Math.max(meta.d, 0.1) + 0.06]} />
          <meshBasicMaterial color="#c9a96e" transparent opacity={0.35} />
        </mesh>
      )}
    </group>
  );
};
